/* eslint-disable no-unused-vars */
import React from 'react';
import { AjaxResponse, ajax } from 'rxjs/ajax';
import { PhotoSeries, WindshieldRepairReplaceDecision } from 'redux/photoseries/photoseries';
import { DamageType, IDamage } from 'redux/damages/damages';
import { ADDITIONAL_SUB_TYPES, IWorkflow, ImageTypeKeys, ImageTypeSettings, WINDSHIELD_SUB_TYPES } from 'redux/workflows/workflows';
import { TRANSLATIONS_VALUES_KEYS } from 'redux/internationalization/internationalization';
import { makeImageUpload, makePost, makeGet, queueAPIRequest } from 'services';
import { Config } from 'utils/camera-photo/media-services';
import { CameraPhoto, IMAGE_TYPES } from 'utils/camera-photo';
import { translateString } from '../../utils/photoseries.util';
import {
  genericErrorNumberDenotation,
  genericErrors,
  getGeolocation,
  CustomError,
  logErrorToServer,
  getDeviceOrientation,
  DeviceOrientationData
} from 'utils';
import {
  PhotoSeriesStoreState,
  CameraSettings,
  CameraMode,
  ImageResponse,
  GenericError,
  CaptureImageState,
  InstructionsState,
  VerificationError,
  NativeCameraPhoto
} from '../root';
import { imageVerificationErrors } from 'utils/constants.util';
import { store } from 'redux/root.store';
import {
  saveDamages,
  updateWindhsieldRepairReplaceDecision
} from 'redux/root.actions';
import { setCaptureInfoLastImageType } from '../capture-info/capture-info.actions';
import { DamageLabellingInstruction } from '../../containers/damage-labelling-instructions/damage-labelling-instructions';
import { getThumbnailUrl } from 'services/thumbnail';

const REQUEST_TIMEOUT_IN_SECONDS = 20;

type SetPhotoSeriesStoreState = React.Dispatch<React.SetStateAction<PhotoSeriesStoreState>>;

const setCaptureImageIsLoading = (isLoading: boolean, setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      isLoading,
    },
  }));
};

const setCaptureImageGenericError = (genericError: GenericError | null, setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => {
    const prevCaptureImage = prevPhotoSeriesStoreState.captureImage;

    // If existing error message has higher (lower value) or same importance then don't overwrite existing error
    if (genericError && prevCaptureImage.genericError && prevCaptureImage.genericError.importance <= genericError.importance) {
      return prevPhotoSeriesStoreState;
    }

    return {
      ...prevPhotoSeriesStoreState,
      captureImage: {
        ...prevCaptureImage,
        genericError,
      },
    };
  });
};

const setCaptureImageHeadingText = (headingText: string, setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      headingText,
    },
  }));
};

const setCaptureImageBottomText = (bottomText: string, setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      bottomText,
    },
  }));
};

const setCaptureImageIsVinEntered = (isVinEntered: boolean, setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      isVinEntered,
    },
  }));
};

const setCaptureImageIsPhotoConfirmed = (isPhotoConfirmed: boolean, setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      isPhotoConfirmed,
    },
  }));
};

const setCaptureImageIsInShootMode = (isInShootMode: boolean, setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => {
    const { cameraPhoto } = prevPhotoSeriesStoreState.captureImage;

    if (isInShootMode && cameraPhoto) cameraPhoto.continueStreaming();

    return {
      ...prevPhotoSeriesStoreState,
      captureImage: {
        ...prevPhotoSeriesStoreState.captureImage,
        isInShootMode,
      },
    };
  });
};

const setCaptureImageIsInDamageLabellingMode = (isInDamageLabellingMode: boolean, setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      isInDamageLabellingMode,
      showDamageLabellingInstructions: isInDamageLabellingMode,
    },
  }));
};

const setCaptureImageDamageLabellingInstructionShown = (
  instruction: DamageLabellingInstruction,
  remove: boolean,
  setPhotoSeriesStoreState: SetPhotoSeriesStoreState
) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      shownDamageLabellingInstructions: remove
        ? prevPhotoSeriesStoreState.captureImage.shownDamageLabellingInstructions.filter((i) => i !== instruction)
        : [...prevPhotoSeriesStoreState.captureImage.shownDamageLabellingInstructions, instruction],
    },
  }));
};

const setCaptureImageImageResponse = (imageResponse: null | ImageResponse, setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      imageResponse
    },
  }));
};

const setCapturedImageObjectUrl = (image: Blob, setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => {
    let imageObjectUrl = prevPhotoSeriesStoreState.captureImage.imageObjectUrl;
    if (imageObjectUrl && imageObjectUrl.length > 0) {
      window.URL.revokeObjectURL(imageObjectUrl);
    }

    imageObjectUrl = window.URL.createObjectURL(image);

    return {
      ...prevPhotoSeriesStoreState,
      captureImage: {
        ...prevPhotoSeriesStoreState.captureImage,
        imageObjectUrl
      },
    };
  });
};

const setCleanCapturedImageObjectUrl = (setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => {
    let imageObjectUrl = prevPhotoSeriesStoreState.captureImage.imageObjectUrl;
    if (imageObjectUrl && imageObjectUrl.length > 0) {
      window.URL.revokeObjectURL(imageObjectUrl);
    }

    return {
      ...prevPhotoSeriesStoreState,
      captureImage: {
        ...prevPhotoSeriesStoreState.captureImage,
        imageObjectUrl: ''
      },
    };
  });
};

const setCaptureImageCameraPhoto = (cameraPhoto: null | CameraPhoto, setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      cameraPhoto,
    },
  }));
};

const setCaptureImageCameraSettings = (cameraSettings: null | CameraSettings, setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      cameraSettings,
    },
  }));
};

const setCaptureImageImageSubTypeTranslationKeys = (
  imageSubTypeTranslationKeys: { [key: number]: string },
  setPhotoSeriesStoreState: SetPhotoSeriesStoreState
) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      imageSubTypeTranslationKeys,
    },
  }));
};

const setCaptureImageAddMoreImagesTotalImagesCaptured = (setPhotoSeriesStoreState: SetPhotoSeriesStoreState, totalImagesCaptured?: number) => {
  setPhotoSeriesStoreState((prevState) => ({
    ...prevState,
    captureImage: {
      ...prevState.captureImage,
      addMoreImages: {
        ...prevState.captureImage.addMoreImages,
        totalImagesCaptured: totalImagesCaptured ?? prevState.captureImage.addMoreImages.totalImagesCaptured + 1,
      },
    },
  }));
};

const setCaptureImageAdditionalImageIndex = (setPhotoSeriesStoreState: SetPhotoSeriesStoreState, additionalImageIndex?: number) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      additionalImageIndex: additionalImageIndex ?? prevPhotoSeriesStoreState.captureImage.additionalImageIndex + 1,
    },
  }));
};

const setCaptureImageCurrentErrorCount = (setPhotoSeriesStoreState: SetPhotoSeriesStoreState, currentErrorCount?: number) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      currentErrorCount: currentErrorCount ?? prevPhotoSeriesStoreState.captureImage.currentErrorCount + 1,
    },
  }));
};

const setCaptureImageItsAddMoreImages = (itsAddMoreImages: boolean, setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevState) => ({
    ...prevState,
    captureImage: {
      ...prevState.captureImage,
      addMoreImages: {
        ...prevState.captureImage.addMoreImages,
        itsAddMoreImages,
      },
    },
  }));
};

const setCaptureImageImageTypeTranslationKeys = (
  imageTypeTranslationKeys: { [key: number]: string },
  setPhotoSeriesStoreState: SetPhotoSeriesStoreState
) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      imageTypeTranslationKeys,
    },
  }));
};

const setCaptureImageIsGdprAccepted = (isGdprAccepted: boolean, setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      isGdprAccepted,
    },
  }));
};

const setCaptureImageNativeCameraPhoto = (
  nativeCameraPhoto: Blob,
  deviceOrientationData: DeviceOrientationData | null,
  setPhotoSeriesStoreState: SetPhotoSeriesStoreState
) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      nativeCameraPhoto: {
        cameraPhoto: nativeCameraPhoto,
        deviceOrientation: deviceOrientationData
      },
    },
  }));
};

const setImageComment = (comment: string, setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      imageComment: comment,
    },
  }));
};

const setCaptureInfoImagesCaptured = (setPhotoSeriesStoreState: SetPhotoSeriesStoreState) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureInfo: {
      ...prevPhotoSeriesStoreState.captureInfo,
      imagesCaptured: prevPhotoSeriesStoreState.captureInfo.imagesCaptured + 1
    },
  }));
};

let imageUploadController: AbortController | null;

const MAX_UPLOAD_SIZE_IN_BYTES = 3 * 1024 * 1024; // 3MB
const MAX_UPLOAD_WIDTH = 4096; // 4K width
const DEFAULT_JPEG_QUALITY = 0.92;
const MIN_JPEG_QUALITY = 0.4;
const JPEG_QUALITY_STEP = 0.05;

const toJpegBlob = (canvas: HTMLCanvasElement, quality: number) =>
  (new Promise<Blob | null>((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality)));

const resizeImageTo4K = async (image: Blob) => {
  const imageBitmap = await createImageBitmap(image);
  const { width, height } = imageBitmap;

  const scale = Math.min(1, MAX_UPLOAD_WIDTH / width);
  const targetWidth = Math.round(width * scale);
  const targetHeight = Math.round(height * scale);

  // If already within limits, keep original dimensions
  if (scale === 1) return { imageBitmap, targetWidth: width, targetHeight: height };

  return { imageBitmap, targetWidth, targetHeight };
};

const compressImageForUpload = async (image: Blob) => {
  try {
    const { imageBitmap, targetWidth, targetHeight } = await resizeImageTo4K(image);

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    ctx?.drawImage(imageBitmap, 0, 0, targetWidth, targetHeight);

    const qualities: number[] = [];
    for (let quality = DEFAULT_JPEG_QUALITY; ; quality = Math.max(MIN_JPEG_QUALITY, quality - JPEG_QUALITY_STEP)) {
      qualities.push(quality);
      if (quality === MIN_JPEG_QUALITY) break;
    }

    const compressedBlob = await qualities.reduce(
      (chain, quality) =>
        chain.then((currentBlob) => {
          if (currentBlob && currentBlob.size <= MAX_UPLOAD_SIZE_IN_BYTES) return currentBlob;
          return toJpegBlob(canvas, quality);
        }),
      Promise.resolve<Blob | null>(null)
    );

    return compressedBlob || image;
  } catch (error) {
    // In case of any failure (e.g. createImageBitmap not supported), fall back to original image
    logErrorToServer('Image compression failed, using original image', error as CustomError);
    return image;
  }
};

interface ProcessSaveImageResponseProps {
  instructions: InstructionsState;
  captureImage: CaptureImageState;
  photoSeries: PhotoSeries;
  isInDamageLabellingMode: boolean;
  onRendererSubTypeView: Function;
  onRenderCaptureInfoView: Function;
  setPhotoSeriesStoreState: SetPhotoSeriesStoreState;
  // eslint-disable-next-line no-unused-vars
  onSetShownValidationModal: (shownValidationModal: boolean) => void;
  imageType: number;
  imageSubType?: number;
  customImageTypeId: string | null;
  cameraMode: CameraMode;
}

const processSaveImageResponse = (props: ProcessSaveImageResponseProps) => {
  const { captureImage, onSetShownValidationModal, setPhotoSeriesStoreState } = props;
  const imageResponse = captureImage.imageResponse;

  //  If any errors returned by API, show feedback modal
  if (imageResponse && imageResponse.verificationErrors?.length) {
    onSetShownValidationModal(true);
    setCaptureImageCurrentErrorCount(setPhotoSeriesStoreState);
    return;
  }

  renderNextView(props);
};

const renderNextView = ({
  instructions,
  captureImage,
  onRendererSubTypeView,
  onRenderCaptureInfoView,
  setPhotoSeriesStoreState,
  imageType,
  imageSubType,
  customImageTypeId,
  photoSeries,
  isInDamageLabellingMode
}: ProcessSaveImageResponseProps) => {
  if (captureImage.currentErrorCount > 0) setCaptureImageCurrentErrorCount(setPhotoSeriesStoreState, 0);

  if (photoSeries.isManualDamageLabellingEnabled(imageType, imageSubType, customImageTypeId) && !isInDamageLabellingMode) {
    setCaptureImageIsInDamageLabellingMode(true, setPhotoSeriesStoreState);
    return;
  }

  const { subType } = instructions;

  setCaptureImageIsInDamageLabellingMode(false, setPhotoSeriesStoreState);
  setCleanCapturedImageObjectUrl(setPhotoSeriesStoreState);

  if (subType && subType.selectedSubTypeIndex !== subType.subTypes.length! - 1) {
    // If ImageType has more ImageSubTypes, render <Instructions/> for next subtype
    onRendererSubTypeView();
    return;
  }

  onRenderCaptureInfoView();
};

const handleRetakePhoto = (setPhotoSeriesStoreState: SetPhotoSeriesStoreState, onAction?: Function) => {
  if (imageUploadController) {
    imageUploadController.abort();
  }

  setCleanCapturedImageObjectUrl(setPhotoSeriesStoreState);
  setCaptureImageGenericError(null, setPhotoSeriesStoreState);
  setCaptureImageImageResponse(null, setPhotoSeriesStoreState);
  setCaptureImageIsPhotoConfirmed(false, setPhotoSeriesStoreState);
  setCaptureImageIsInShootMode(true, setPhotoSeriesStoreState);

  if (onAction) onAction();
};

interface HandleImageUploadCompletedProps extends ProcessSaveImageResponseProps {
  isInShootMode: boolean;
  isPhotoConfirmed: boolean;
  onActionAfterImageUploadAjax?: Function;
  handleRetakeAction?: Function;
}

const handleImageUploadCompleted = (response: any, props: HandleImageUploadCompletedProps) => {
  if (imageUploadController) {
    imageUploadController.abort();
  }

  const isNative = props.cameraMode === CameraMode.Native;
  const isExteriorOrWindshield = props.imageType === ImageTypeKeys.Exterior || props.imageType === ImageTypeKeys.Windshield;
  const shouldFilterPortraitImageError = !isNative || !isExteriorOrWindshield;

  const filteredResponse = shouldFilterPortraitImageError
    ? {
      ...response,
      verificationErrors: response.verificationErrors?.filter((e: VerificationError) => e.error !== imageVerificationErrors.portraitImage)
    }
    : response;

  setCaptureImageImageResponse(filteredResponse, props.setPhotoSeriesStoreState);
  props.onActionAfterImageUploadAjax?.(filteredResponse.imageId, filteredResponse.verificationErrors as VerificationError[]);

  if (props.isPhotoConfirmed) {
    processSaveImageResponse(props);
  }

  // set is loading to false here
  setCaptureImageIsLoading(false, props.setPhotoSeriesStoreState);
  setCaptureInfoLastImageType(props.imageType, props.setPhotoSeriesStoreState);
};

interface UploadImageProps extends HandleImageUploadCompletedProps {
  photoSeriesId: string;
  data: Record<string, unknown>;
  image: Blob;
  imageType: number;
  imageSubType?: number;
  handleRetakeAction?: Function;
}

const uploadImage = (props: UploadImageProps) => {
  const { photoSeriesId, data, image, setPhotoSeriesStoreState, handleRetakeAction } = props;

  const handleError = (errorObj: CustomError) => {
    if (imageUploadController?.signal?.aborted) {
      imageUploadController = null;
      return;
    }

    const { vehicle_scan, server_error } = TRANSLATIONS_VALUES_KEYS;

    logErrorToServer('Error occurred when trying to upload image to API', errorObj);
    setCaptureImageGenericError(
      {
        errors: [{ error: genericErrorNumberDenotation.genericError, errorDetails: genericErrors.serverError }],
        onAction: () => handleRetakePhoto(setPhotoSeriesStoreState, handleRetakeAction),
        text: translateString(vehicle_scan.retry_btn),
        title: translateString(server_error.title),
        importance: 1,
      },
      setPhotoSeriesStoreState
    );
  };

  if (imageUploadController) {
    imageUploadController.abort();
  }

  imageUploadController = new AbortController();
  const timeoutId = setTimeout(() => {
    if (imageUploadController) {
      imageUploadController.abort();
    }

    logErrorToServer('"SaveImage" request timed out when trying to upload image to API', null);

    const { vehicle_scan, timeout_error } = TRANSLATIONS_VALUES_KEYS;

    setCaptureImageGenericError(
      {
        errors: [{ error: genericErrorNumberDenotation.genericError, errorDetails: genericErrors.timeoutError }],
        onAction: () => handleRetakePhoto(setPhotoSeriesStoreState, handleRetakeAction),
        text: translateString(vehicle_scan.retry_btn),
        title: translateString(timeout_error.title),
        importance: 1,
      },
      setPhotoSeriesStoreState
    );
  }, REQUEST_TIMEOUT_IN_SECONDS * 1000);

  const startUpload = (preparedImage: Blob) => {
    queueAPIRequest(() => makeImageUpload(`capture/${photoSeriesId}/images`, preparedImage, data, imageUploadController!.signal))
      .then((response: Response) => {
        if (!response.ok) {
          throw new Error('Received not ok response!');
        }

        return response.json();
      })
      .then((response: any) => {
        clearTimeout(timeoutId);
        handleImageUploadCompleted(response, props);
      })
      .catch((error: any) => {
        clearTimeout(timeoutId);
        setCaptureImageIsLoading(false, setPhotoSeriesStoreState);
        handleError(error as CustomError);
      });
  };

  compressImageForUpload(image)
    .then((preparedImage) => startUpload(preparedImage))
    .catch((error) => {
      // If compression fails for any reason, upload the original image to avoid blocking the flow
      logErrorToServer('Image compression threw an error, uploading original image', error as CustomError);
      startUpload(image);
    });
};

interface OnTakePhoto extends UploadImageProps {
  imageType: number;
  imageSubType?: number;
  customImageTypeId: string | null;
  sequenceNo?: number;
}

type RelatedObject = {
  id: string,
  imageId: string,
  type: RelatedObjectType
}

enum RelatedObjectType {
  Damage = 1
}

interface OnTakePhotoProps extends Omit<OnTakePhoto, 'data' | 'photoSeriesId'> {
  geolocationEnabled: boolean;
  relatedObject?: RelatedObject | undefined
  orientationData: DeviceOrientationData | null;
  handleRetakeAction?: Function;
}

const onTakePhoto = (props: OnTakePhotoProps) => {
  const {
    image,
    imageType,
    imageSubType,
    customImageTypeId,
    photoSeries,
    onActionAfterImageUploadAjax,
    geolocationEnabled,
    sequenceNo,
    relatedObject,
    setPhotoSeriesStoreState,
    orientationData
  } = props;

  // Set capture image object url
  setCapturedImageObjectUrl(image, setPhotoSeriesStoreState);

  const data: Record<string, number | undefined | Record<string, number> | Record<string, string | boolean | number | null> | string> = {
    imageType,
    imageSubType
  };

  if (imageType === ImageTypeKeys.Exterior || imageType === ImageTypeKeys.Windshield || imageType === ImageTypeKeys.Interior) {
    data.imageSubType = imageSubType;
  } else if (imageType === ImageTypeKeys.Additional) {
    data.sequenceNo = sequenceNo;
    if (relatedObject) {
      data.relatedObject = {
        id: relatedObject.id,
        type: relatedObject.type.toString(),
        imageId: relatedObject.imageId
      };
    }
  } else if (imageType === ImageTypeKeys.Custom && customImageTypeId !== null) {
    delete data.imageSubType;
    data.customImageSubTypeId = customImageTypeId;
    data.sequenceNo = sequenceNo;
  }

  const hasValidOrientationData = (data: DeviceOrientationData | null): boolean => {
    if (!data) return false;
    return data.alpha !== null || data.beta !== null || data.gamma !== null;
  };

  if (hasValidOrientationData(orientationData)) {
    data.deviceOrientation = {
      absolute: orientationData!.absolute,
      alpha: orientationData!.alpha,
      beta: orientationData!.beta,
      gamma: orientationData!.gamma
    };
  }

  const uploadImageProps = {
    data,
    photoSeriesId: photoSeries.id,
    onActionAfterImageUploadAjax,
    ...props,
  };

  if (geolocationEnabled) {
    getGeolocation(
      ({ coords: { accuracy, latitude, longitude } }) => {
        data.location = {
          accuracy,
          latitude,
          longitude,
        };
        uploadImage(uploadImageProps);
      },
      () => uploadImage(uploadImageProps),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 4000 }
    );
  } else {
    uploadImage(uploadImageProps);
  }
};

interface HandleCameraButtonClick extends UploadImageProps {
  imageType: number;
  imageSubType?: number;
  customImageTypeId: string | null;
  sequenceNo?: number;
  cameraPhoto: null | CameraPhoto;
  photoSeries: PhotoSeries;
}

export interface HandleCameraButtonClickProps extends Omit<HandleCameraButtonClick, 'data' | 'photoSeriesId' | 'image'> {
  geolocationEnabled: boolean;
  draftDamages?: IDamage[];
  relatedObject?: RelatedObject | undefined,
  workflow: IWorkflow,
  handleRetakeAction?: Function;
}

const handleShootModeClick = (props: HandleCameraButtonClickProps) => {
  // Photo has not been taken yet
  const {
    setPhotoSeriesStoreState,
    cameraPhoto,
  } = props;

  if (!cameraPhoto) return;

  setCaptureImageIsLoading(true, setPhotoSeriesStoreState);
  setCaptureImageIsInShootMode(false, setPhotoSeriesStoreState);
  setCaptureImageImageResponse(null, setPhotoSeriesStoreState);

  const config = { imageType: IMAGE_TYPES.JPG, imageCompression: 0.5 };

  Promise.all([
    cameraPhoto.getImage(config as Config),
    !props.photoSeries.trackDeviceOrientationEnabled
      ? Promise.resolve(null)
      : getDeviceOrientation()
  ]).then(([imageResponse, orientationData]) => {
    const { forUpload } = imageResponse as { forUpload: Blob | null };

    onTakePhoto({
      ...props,
      image: forUpload!,
      orientationData,
      onActionAfterImageUploadAjax(imageId: string | null, verificationErrors: VerificationError[] | null) {
        props.onActionAfterImageUploadAjax?.(imageId, verificationErrors);
      },
    });
  });
};

const handleConfirmPhotoClick = (props: HandleCameraButtonClickProps) => {
  const {
    photoSeries,
    imageType,
    captureImage,
    setPhotoSeriesStoreState,
  } = props;

  setCaptureImageIsPhotoConfirmed(true, setPhotoSeriesStoreState);

  if (!captureImage.isLoading && !captureImage.isInShootMode) {
    // Image has been saved and user clicked confirm btn
    const additionalImageRequired = imageType === ImageTypeKeys.Additional || captureImage.addMoreImages.itsAddMoreImages;
    if (additionalImageRequired) {
      setCaptureImageAddMoreImagesTotalImagesCaptured(setPhotoSeriesStoreState, captureImage.addMoreImages.totalImagesCaptured + 1);
      setCaptureImageAdditionalImageIndex(setPhotoSeriesStoreState, captureImage.additionalImageIndex + 1);
    }

    handleSaveImageComment(photoSeries.id, captureImage?.imageResponse?.imageId, captureImage?.imageComment, setPhotoSeriesStoreState);
    setCaptureInfoImagesCaptured(setPhotoSeriesStoreState);
    processSaveImageResponse(props);
  }
};

const handleDamageLabellingModeClick = (props: HandleCameraButtonClickProps) => {
  const {
    photoSeries: {
      id: photoSeriesId
    },
    captureImage: {
      isLoading,
      imageResponse: image,
      imageComment
    },
    draftDamages,
    workflow,
    setPhotoSeriesStoreState,
  } = props;

  if (isLoading) return;

  if (draftDamages && draftDamages.length > 0) {
    // Check if closeups are reqiuired
    const additionalSettings = workflow.imageTypeSettings.find((s: ImageTypeSettings) => s.enabled && s.imageType === ImageTypeKeys.Additional);
    const isCloseUpsEnabled = additionalSettings &&
      additionalSettings.imageSubTypes.some((cfg: any) => cfg.imageSubType === ADDITIONAL_SUB_TYPES.FIRST_CLOSE_UP);
    const analyzeWindshieldFromOutside = props.photoSeries.windshieldRepairReplaceDecisionEnabled &&
      props.imageType === ImageTypeKeys.Windshield &&
      props.imageSubType === WINDSHIELD_SUB_TYPES.FROM_OUTSIDE;
    const detectWindshieldRepairability = analyzeWindshieldFromOutside && !isCloseUpsEnabled;
    const aILocationAssistanceEnabled = props.photoSeries.manualLabellingWindshieldDamageDetection ||
                                        props.photoSeries.aiLocationAssistanceEnabled;

    setCaptureImageIsLoading(true, setPhotoSeriesStoreState);

    const windshieldRepairability = () => {
      if (detectWindshieldRepairability) {
        store.dispatch(updateWindhsieldRepairReplaceDecision(WindshieldRepairReplaceDecision.Calculating));
        queueAPIRequest(() => makeGet(`capture/${photoSeriesId}/detect-ws-repairability`))
          .then((response: AjaxResponse<any>) => store.dispatch(updateWindhsieldRepairReplaceDecision(response.response)))
          .catch(() => store.dispatch(updateWindhsieldRepairReplaceDecision(WindshieldRepairReplaceDecision.Uncertain)));
      }
    };

    makePost(
      `capture/${photoSeriesId}/images/${image!.imageId}/damages`,
      filterDraftDamages(draftDamages)
    ).subscribe(({ response }: AjaxResponse<any>) => {
      const damages = response as IDamage[];
      store.dispatch(saveDamages(image!.imageId, damages, props.imageSubType));

      if (aILocationAssistanceEnabled) {
        queueAPIRequest(() => makeGet(`capture/${photoSeriesId}/analyze-ws-and-user-marked-damages`))
          .then((data: any) => {
            // Update damage types
            const damages = data.response as IDamage[];
            store.dispatch(saveDamages(image!.imageId, damages, props.imageSubType));
            windshieldRepairability();
          });
      } else {
        windshieldRepairability();
      }

      // Cache damage thumbnails for better UX
      damages.forEach((damage) => {
        getThumbnailUrl(damage.imageFileName, damage.id, true)
          .then((url) => {
            ajax({
              url,
              method: 'GET'
            }).subscribe(
              () => {},
              () => {}
            );
          })
          .catch(() => {});
      });

      setCaptureImageIsLoading(false, setPhotoSeriesStoreState);
      renderNextView(props);
    });
  } else {
    renderNextView(props);
  }

  handleSaveImageComment(photoSeriesId, image?.imageId, imageComment, setPhotoSeriesStoreState);
};

const doChipDamagesOverlap = (a: IDamage, b: IDamage): boolean => {
  const [minA, maxA] = a.damageLocation.mask;
  const [minB, maxB] = b.damageLocation.mask;
  return minA.x <= maxB.x && maxA.x >= minB.x && minA.y <= maxB.y && maxA.y >= minB.y;
};

/*
* Filters out draft damages that should not be sent to the API.
* We want to filter out overlapping chip damages and keep the first marked chip damage if overlapping is chained.
*/
const filterDraftDamages = (damages: IDamage[]): IDamage[] => {
  const chipDamages = damages.filter((damage) => damage.damageType === DamageType.Chip);
  const nonChipDamages = damages.filter((damage) => damage.damageType !== DamageType.Chip);

  const finalChipDamages: IDamage[] = [];

  // Union-Find: group chips that overlap (directly or transitively) and keep only the first in each group.
  const parent = chipDamages.map((_, i) => i);

  const find = (i: number): number => {
    if (parent[i] !== i) parent[i] = find(parent[i]);
    return parent[i];
  };

  for (let i = 0; i < chipDamages.length; i++) {
    for (let j = i + 1; j < chipDamages.length; j++) {
      if (doChipDamagesOverlap(chipDamages[i], chipDamages[j])) {
        const rootI = find(i);
        const rootJ = find(j);
        if (rootI !== rootJ) {
          parent[Math.max(rootI, rootJ)] = Math.min(rootI, rootJ);
        }
      }
    }
  }

  chipDamages.forEach((chipDamage, index) => {
    if (find(index) === index) {
      finalChipDamages.push(chipDamage);
    }
  });

  return [
    ...nonChipDamages,
    ...finalChipDamages
  ];
};

const handleCameraButtonClick = (props: HandleCameraButtonClickProps) => {
  const {
    isInShootMode,
    isPhotoConfirmed,
    isInDamageLabellingMode
  } = props;

  if (isInShootMode) {
    handleShootModeClick(props);
  } else if (isInDamageLabellingMode) {
    handleDamageLabellingModeClick(props);
  } else if (!isPhotoConfirmed) {
    handleConfirmPhotoClick(props);
  }
};

const handleNativeCameraPhotoTaken = (
  setPhotoSeriesStoreState: SetPhotoSeriesStoreState,
  photo: Blob,
  deviceOrientationData: DeviceOrientationData | null
) => {
  setCaptureImageNativeCameraPhoto(photo, deviceOrientationData, setPhotoSeriesStoreState);
};

const uploadNativeCameraPhoto = (props: HandleCameraButtonClickProps, nativeCameraPhoto: NativeCameraPhoto) => {
  // Photo has not been uploaded yet
  const {
    setPhotoSeriesStoreState,
  } = props;

  setCaptureImageIsLoading(true, setPhotoSeriesStoreState);
  setCaptureImageIsInShootMode(false, setPhotoSeriesStoreState);
  setCaptureImageImageResponse(null, setPhotoSeriesStoreState);

  onTakePhoto({
    ...props,
    image: nativeCameraPhoto.cameraPhoto!,
    orientationData: nativeCameraPhoto.deviceOrientation,
    onActionAfterImageUploadAjax(imageId: string | null, verificationErrors: VerificationError[] | null) {
      props.onActionAfterImageUploadAjax?.(imageId, verificationErrors);
    },
  });
};

const handleSaveImageComment = (
  photoSeriesId: string,
  imageId: string | undefined | null,
  comment: string | undefined,
  setPhotoSeriesStoreState: SetPhotoSeriesStoreState
) => {
  if (imageId && comment && comment.length > 0) {
    makePost(`capture/${photoSeriesId}/images/${imageId}/comment`, { text: comment }).subscribe();
  }

  setImageComment('', setPhotoSeriesStoreState);
};

const setCaptureImageRetakeCount = (
  retakeCount: number,
  setPhotoSeriesStoreState: SetPhotoSeriesStoreState
) => {
  setPhotoSeriesStoreState((prevPhotoSeriesStoreState) => ({
    ...prevPhotoSeriesStoreState,
    captureImage: {
      ...prevPhotoSeriesStoreState.captureImage,
      retakeCount
    },
  }));
};

export {
  setCaptureImageIsLoading,
  setCaptureImageGenericError,
  setCaptureImageBottomText,
  setCaptureImageHeadingText,
  setCaptureImageImageResponse,
  setCaptureImageIsPhotoConfirmed,
  setCaptureImageIsInShootMode,
  setCaptureImageIsInDamageLabellingMode,
  setCaptureImageDamageLabellingInstructionShown,
  setCaptureImageImageTypeTranslationKeys,
  setCaptureImageImageSubTypeTranslationKeys,
  setCaptureImageCameraPhoto,
  setCaptureImageCameraSettings,
  setCaptureImageIsGdprAccepted,
  handleCameraButtonClick,
  handleRetakePhoto,
  setCaptureImageIsVinEntered,
  setCaptureImageAddMoreImagesTotalImagesCaptured,
  setCaptureImageAdditionalImageIndex,
  setCaptureImageItsAddMoreImages,
  setCaptureImageCurrentErrorCount,
  handleNativeCameraPhotoTaken,
  setCaptureImageNativeCameraPhoto,
  uploadNativeCameraPhoto,
  setCleanCapturedImageObjectUrl,
  RelatedObjectType,
  setImageComment,
  handleSaveImageComment,
  setCaptureImageRetakeCount
};

export type { HandleCameraButtonClick };
